import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckoutService } from '../../core/checkout/checkout.service';
import { VisitService } from '../../core/visit/visit.service';
import { QrErrorCopy, qrErrorCopy } from '../../core/visit/qr-error-copy';
import { FooterComponent } from '../../layout/footer/footer.component';
import { HeaderComponent } from '../../layout/header/header.component';
import { FooterNavComponent } from '../../shared/components/footer-nav/footer-nav.component';
import { IdentificationCardComponent } from '../../shared/components/identification-card/identification-card.component';

@Component({
  selector: 'app-qr-error',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    FooterNavComponent,
    IdentificationCardComponent,
  ],
  templateUrl: './qr-error.component.html',
  styleUrl: './qr-error.component.scss',
})
export class QrErrorComponent {
  private readonly router          = inject(Router);
  private readonly route           = inject(ActivatedRoute);
  private readonly visitService    = inject(VisitService);
  private readonly checkoutService = inject(CheckoutService);

  readonly retryCardTitle = "Scanner le QR Code\nde nouveau";

  /** Écran partagé par les deux parcours ; la route porte lequel. */
  readonly isCheckout = this.route.snapshot.data['journey'] === 'checkout';

  /**
   * Snapshot of the failure copy, captured ONCE when this screen mounts. The
   * ScanQrComponent has already recorded the failure on the visit flow before
   * navigating here, so no reactivity is needed — and a live computed would
   * flash the generic "QR code non reconnu" copy when the inactivity timeout
   * ends the visit (clearing the flow) just before redirecting to the accueil.
   */
  readonly copy = signal<QrErrorCopy>(
    this.isCheckout
      ? {
          title: this.checkoutService.error()?.title ?? 'QR code non reconnu',
          subtitle: this.checkoutService.error()?.subtitle ?? 'Veuillez réessayer de nouveau',
        }
      : qrErrorCopy(this.visitService.current()?.flow),
  );

  /**
   * Côté arrivée, le seul échec que rescanner ne peut pas résoudre : le pass est
   * valide, mais son arrivée du jour est déjà enregistrée. Figé à l'affichage
   * pour la même raison que `copy` — le timeout d'inactivité vide le flow juste
   * avant de rediriger vers l'accueil, et un `computed` ferait réapparaître le
   * bouton au passage.
   */
  private readonly checkinAllowsRetry =
    this.visitService.current()?.flow.qrScanErrorType !== 'already_checked_in';

  /**
   * Le bouton de rescan disparaît quand réessayer ne peut rien changer — un QR
   * déjà utilisé pour un départ (spec §4.1.2) ou pour une arrivée encore
   * ouverte. L'offrir serait un cul-de-sac.
   */
  readonly showRetry = computed(() =>
    this.isCheckout
      ? (this.checkoutService.error()?.allowRetry ?? true)
      : this.checkinAllowsRetry,
  );

  readonly scanRoute = this.isCheckout ? '/checkout/scan-qr' : '/scan-qr';

  constructor() {
    // « Arrivée déjà signalée » est un écran de fin de parcours, comme la
    // confirmation d'arrivée : la session est clôturée et enregistrée dès
    // l'affichage (le message ci-dessus est déjà figé). Les autres refus —
    // RDV en avance, annulé, passé — restent dans la session en cours : une
    // seule visite, clôturée par le timeout ou par la confirmation.
    if (!this.isCheckout && !this.checkinAllowsRetry) {
      void this.visitService.endVisit('OnClick');
    }
  }

  retryScan(): void {
    void this.router.navigate([this.scanRoute]);
  }
}
